import React, { useEffect, useState } from "react";
import ProgressMeter from "src/components/progressMeter";
import styles from "./styles.module.scss";

interface ScoreMeterProps {
  animatedValue: number;
}

const ScoreMeter: React.FC<ScoreMeterProps> = ({ animatedValue }) => {
    type SpeedometerSegment = {
    name: string;
    value: number;
    fill: string;
    isActive: boolean;
    stroke: string;
  };

    const SEGMENT_COUNT = 50;

    const segmentColors = Array(SEGMENT_COUNT)
    .fill(null)
    .map((_, i) => {
      if (i % 2 === 0) {
        return "#FAFAFA";
      } else {
        if (i > 37) {
          return "#00800099";
        } else if (i > 25) {
          return "#FFA50099";
        } else if (i > 12) {
          return "#FFA50099";
        } else {
          return "#FF000066";
        }
      }
    });

  const [showScore, setShowScore] = useState(false);

  useEffect(() => {
    setShowScore(false);

    // Delay the score display to allow the animation to complete
    const animationDuration = 2000;
    const timer = setTimeout(() => {
      setShowScore(true);
    }, animationDuration);

    return () => clearTimeout(timer);
  }, [animatedValue]);

  const createSpeedometerData = (value: number): SpeedometerSegment[] => {
    const progressValue = Math.max(0, Math.min(100, value));
    const segments: SpeedometerSegment[] = [];
    const totalSegments = 50;
    const segmentsToFill = Math.round((progressValue / 100) * totalSegments);

    for (let i = 0; i < totalSegments; i++) {
      segments.push({
        name: `Segment ${i}`,
        value: 1,
        fill:
          i < segmentsToFill
            ? segmentColors[i]
            : i % 2 === 0
              ? "#FAFAFA"
              : "#e5e7eb",
        isActive: i < segmentsToFill,
        stroke: '#FAFAFA'
      });
    }

    return segments;
  };

  const getScoreColor = (percentage: number): string => {
    if (percentage > 75) {
      return "#008000";
    } else if (percentage > 50) {
      return "#FFA500";
    } else if (percentage > 25) {
      return "#FFA500";
    } else {
      return "#FF0000";
    }
  };

 const calculateLabelPosition = (
    percentage,
    containerWidth = 200,
    containerHeight = 140,
  ) => {
    const centerX = containerWidth / 2;
    const centerY = containerHeight * 0.92;
    
    let radius;
    if (percentage === 0 || percentage === 100) {
      radius = 85;
    } else {
      radius = 75;
    }
    
    const startAngle = 185; 
    const endAngle = -5;  
    const angleRange = startAngle - endAngle;
    
    const angle = (startAngle - (percentage / 100) * angleRange) * (Math.PI / 180);

    const x = centerX + radius * Math.cos(angle);
    const y = centerY - radius * Math.sin(angle);

    return { x, y };
  };

  const getScore = () => {
    return animatedValue > 75
      ? "Optimized"
      : animatedValue > 50
      ? "Stable"
      : animatedValue > 25
      ? "At risk"
      : "Critical";
  }

  const labelPositions = [
    { percentage: 0, label: "0" },
    { percentage: 25, label: "25" },
    { percentage: 50, label: "50" },
    { percentage: 75, label: "75" },
    { percentage: 100, label: "100" },
  ].map((item) => ({
    ...item,
    position: calculateLabelPosition(item.percentage),
  }));

  const data = createSpeedometerData(animatedValue);

  return (
    <>
    <ProgressMeter data={data} />

      <div className={styles.labels}>
        {labelPositions.map(({ label, position, percentage }) => (
          <div
            key={percentage}
            className={styles.label}
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
            }}
          >
            {label}
          </div>
        ))}
      </div>

      <div className={styles.scoreCountContainer}>
        <div className={styles.scoreCount}>
          <span className={styles.count}>{Math.round(animatedValue)}%</span>
            <span 
              style={{ color: getScoreColor(animatedValue) }}
              className={styles.scoreLabel}
            >
              {showScore && getScore()}
            </span>
        </div>
      </div>
    </>
  );
};

export default ScoreMeter;